import React, { useEffect, useMemo, useRef } from "react";
import {
  AchievementRibbon,
  Aurora,
  AuroraBlob,
  Availability,
  Background,
  CertCard,
  CertGrid,
  CertIcon,
  CertText,
  Column,
  Container,
  ContactIcon,
  ContactLink,
  ContactList,
  FontImport,
  GlassCard,
  Grid,
  GridOverlay,
  Header,
  Hexagon,
  HexagonInner,
  HeroBadge,
  LevelTag,
  Meter,
  Muted,
  OrbitRing,
  PageWrapper,
  Particle,
  Particles,
  ProjectCard,
  ProjectGrid,
  ProjectHead,
  ProjectLink,
  ProjectTitle,
  SectionTitle,
  SkillBar,
  SkillHead,
  SkillRow,
  Small,
  Spotlight,
  StackRow,
  StackTag,
  SubTitle,
  Tag,
  TagCloud,
  TechCard,
  TechCardTitle,
  TechGrid,
  Timeline,
  TimelineBody,
  TimelineItem,
  TimelineMeta,
  TimelineRole,
  Title,
  TitleBlock,
  Underline
} from "./AboutMe.styles";

import {
  FaCode,
  FaCogs,
  FaDatabase,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGithub,
  FaGlobe,
  FaGraduationCap,
  FaLeaf,
  FaLinkedinIn,
  FaNodeJs,
  FaServer,
  FaShieldAlt,
  FaTrophy,
  FaUserAstronaut,
  FaWhatsapp
} from "react-icons/fa";

import { GlassCardInner } from "./GlassCardInner";
import { RevealSection } from "./RevealSection";
import { TechOrbit } from "./TechOrbit";
import { handleTiltLeave, handleTiltMove } from "./tilt";
import { profileData } from "./profile.data";

const iconForSocial = (label: string) => {
  const l = label.toLowerCase();
  if (l.includes("email")) return <FaEnvelope size={18} />;
  if (l.includes("linkedin")) return <FaLinkedinIn size={18} />;
  if (l.includes("github")) return <FaGithub size={18} />;
  if (l.includes("whatsapp")) return <FaWhatsapp size={18} />;
  if (l.includes("site")) return <FaGlobe size={18} />;
  return <FaGlobe size={18} />;
};

const PARTICLE_COLORS = ["#22d3ee", "#60a5fa", "#8b5cf6", "#67e8f9"];

const AboutMe: React.FC = () => {
  const auroraRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: Math.round(Math.random() * 100),
        size: +(2 + Math.random() * 4).toFixed(1),
        duration: +(14 + Math.random() * 18).toFixed(1),
        delay: +(Math.random() * -24).toFixed(1),
        color: PARTICLE_COLORS[i % PARTICLE_COLORS.length]
      })),
    []
  );

  const handlePointerMove = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.setProperty("--x", `${e.clientX}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY}px`);
  };

  // Efeito parallax: as camadas de fundo se deslocam em velocidades
  // diferentes da rolagem da página, criando sensação de profundidade.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (auroraRef.current) {
        auroraRef.current.style.transform = `translate3d(0, ${y * 0.04}px, 0)`;
      }
      if (gridRef.current) {
        gridRef.current.style.transform = `translate3d(0, ${y * 0.05}px, 0)`;
      }
      if (particlesRef.current) {
        particlesRef.current.style.transform = `translate3d(0, ${
          y * -0.03
        }px, 0)`;
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <PageWrapper onMouseMove={handlePointerMove}>
      <FontImport />

      {/* Fundo animado */}
      <Background>
        <Aurora ref={auroraRef}>
          <AuroraBlob
            $color="#22d3ee"
            $size="40vw"
            $top="0%"
            $left="-5%"
            $duration={26}
            $delay={0}
          />
          <AuroraBlob
            $color="#8b5cf6"
            $size="34vw"
            $top="15%"
            $left="55%"
            $duration={32}
            $delay={-8}
          />
          <AuroraBlob
            $color="#3b82f6"
            $size="30vw"
            $top="55%"
            $left="20%"
            $duration={28}
            $delay={-14}
          />
        </Aurora>
        <GridOverlay ref={gridRef} />
        <Particles ref={particlesRef}>
          {particles.map((p) => (
            <Particle
              key={p.id}
              $left={p.left}
              $size={p.size}
              $duration={p.duration}
              $delay={p.delay}
              $color={p.color}
            />
          ))}
        </Particles>
      </Background>

      <Spotlight />

      <Container>
        {/* Header */}
        <Header>
          <TitleBlock>
            <Title>{profileData.fullName}</Title>
            <Underline />
            <SubTitle>{profileData.headline}</SubTitle>
          </TitleBlock>

          <HeroBadge>
            <OrbitRing />
            <Hexagon>
              <HexagonInner>
                <FaCode size={64} color="#22d3ee" />
              </HexagonInner>
            </Hexagon>
            <Availability>DISPONÍVEL</Availability>
          </HeroBadge>
        </Header>

        {/* Grid principal */}
        <Grid>
          {/* Coluna Esquerda */}
          <Column>
            {/* SOBRE MIM */}
            <RevealSection>
              <GlassCard
                $accent
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
              >
                <GlassCardInner>
                  <SectionTitle>
                    <FaUserAstronaut />
                    SOBRE MIM
                  </SectionTitle>
                  {profileData.about.map((p, i) => (
                    <Muted
                      key={i}
                      style={{
                        marginBottom:
                          i === profileData.about.length - 1 ? 0 : "1rem",
                        fontSize: "1.4rem"
                      }}
                    >
                      {p}
                    </Muted>
                  ))}
                </GlassCardInner>
              </GlassCard>
            </RevealSection>

            {/* HABILIDADES TÉCNICAS */}
            <RevealSection delay={0.08}>
              {(inView) => (
                <GlassCard
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                >
                  <GlassCardInner>
                    <SectionTitle>
                      <FaCogs />
                      HABILIDADES TÉCNICAS
                    </SectionTitle>

                    <TechGrid>
                      {/* Backend */}
                      <TechCard
                        onMouseMove={handleTiltMove}
                        onMouseLeave={handleTiltLeave}
                      >
                        <TechCardTitle>
                          <FaServer />
                          Backend
                        </TechCardTitle>

                        {profileData.skills.backend.map((s) => (
                          <SkillRow key={s.name}>
                            <SkillHead>
                              <span>{s.name}</span>
                              <LevelTag>{s.level}%</LevelTag>
                            </SkillHead>
                            <Meter>
                              <SkillBar $level={s.level} $show={inView} />
                            </Meter>
                          </SkillRow>
                        ))}
                      </TechCard>

                      {/* Bancos de Dados */}
                      <TechCard
                        onMouseMove={handleTiltMove}
                        onMouseLeave={handleTiltLeave}
                      >
                        <TechCardTitle>
                          <FaDatabase />
                          Bancos de Dados
                        </TechCardTitle>

                        {profileData.skills.databases.map((s) => (
                          <SkillRow key={s.name}>
                            <SkillHead>
                              <span>{s.name}</span>
                              <LevelTag>{s.level}%</LevelTag>
                            </SkillHead>
                            <Meter>
                              <SkillBar $level={s.level} $show={inView} />
                            </Meter>
                          </SkillRow>
                        ))}
                      </TechCard>

                      {/* Linguagens */}
                      <TechCard
                        onMouseMove={handleTiltMove}
                        onMouseLeave={handleTiltLeave}
                      >
                        <TechCardTitle>
                          <FaCode />
                          Linguagens
                        </TechCardTitle>

                        {profileData.skills.linguagens.map((s) => (
                          <SkillRow key={s.name}>
                            <SkillHead>
                              <span>{s.name}</span>
                              <LevelTag>{s.level}%</LevelTag>
                            </SkillHead>
                            <Meter>
                              <SkillBar $level={s.level} $show={inView} />
                            </Meter>
                          </SkillRow>
                        ))}
                      </TechCard>

                      {/* ORM/ODM */}
                      <TechCard
                        onMouseMove={handleTiltMove}
                        onMouseLeave={handleTiltLeave}
                      >
                        <TechCardTitle>
                          <FaShieldAlt />
                          ORM/ODM
                        </TechCardTitle>

                        {profileData.skills.orm.map((s) => (
                          <SkillRow key={s.name}>
                            <SkillHead>
                              <span>{s.name}</span>
                              <LevelTag>{s.level}%</LevelTag>
                            </SkillHead>
                            <Meter>
                              <SkillBar $level={s.level} $show={inView} />
                            </Meter>
                          </SkillRow>
                        ))}
                      </TechCard>

                      {/* Outras Tecnologias */}
                      <TechCard
                        onMouseMove={handleTiltMove}
                        onMouseLeave={handleTiltLeave}
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <TechCardTitle>
                          <FaLeaf />
                          Outras Tecnologias
                        </TechCardTitle>

                        <TagCloud>
                          {profileData.skills.others.map((o) => (
                            <Tag key={o}>
                              <FaLeaf size={13} />
                              {o}
                            </Tag>
                          ))}
                        </TagCloud>
                      </TechCard>
                    </TechGrid>
                  </GlassCardInner>
                </GlassCard>
              )}
            </RevealSection>

            {/* PROJETOS */}
            {profileData.projects.length > 0 && (
              <RevealSection delay={0.12}>
                <GlassCard
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                >
                  <GlassCardInner>
                    <SectionTitle>
                      <FaCode />
                      PROJETOS
                    </SectionTitle>

                    <ProjectGrid>
                      {profileData.projects.map((p) => (
                        <ProjectCard
                          key={p.name}
                          onMouseMove={handleTiltMove}
                          onMouseLeave={handleTiltLeave}
                        >
                          <ProjectHead>
                            <ProjectTitle>{p.name}</ProjectTitle>
                            {p.period && <Small>{p.period}</Small>}
                          </ProjectHead>

                          {p.role && <Small>{p.role}</Small>}

                          {p.description && (
                            <Muted
                              style={{ marginTop: ".5rem", fontSize: "1.3rem" }}
                            >
                              {p.description}
                            </Muted>
                          )}

                          {p.techs && p.techs.length > 0 && (
                            <StackRow>
                              {p.techs.map((t) => (
                                <StackTag key={t}>{t}</StackTag>
                              ))}
                            </StackRow>
                          )}

                          {p.url && (
                            <ProjectLink
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Ver projeto <FaExternalLinkAlt size={12} />
                            </ProjectLink>
                          )}
                        </ProjectCard>
                      ))}
                    </ProjectGrid>
                  </GlassCardInner>
                </GlassCard>
              </RevealSection>
            )}
          </Column>

          {/* Coluna Direita */}
          <Column>
            {/* TECH STACK (Ícones Orbitando) */}
            <RevealSection>
              <GlassCard
                $accent
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
              >
                <GlassCardInner>
                  <SectionTitle>
                    <FaNodeJs />
                    TECH STACK
                  </SectionTitle>

                  <TechOrbit />
                </GlassCardInner>
              </GlassCard>
            </RevealSection>

            {/* EXPERIÊNCIA */}
            <RevealSection delay={0.06}>
              <GlassCard
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
              >
                <GlassCardInner>
                  <SectionTitle>
                    <FaServer />
                    EXPERIÊNCIA
                  </SectionTitle>

                  <Timeline>
                    {profileData.experience.map((e) => (
                      <TimelineItem key={`${e.company}-${e.role}`}>
                        <TimelineRole>{e.role}</TimelineRole>
                        <TimelineMeta>
                          <span>{e.company}</span>
                          {e.location && <span>• {e.location}</span>}
                          <span>
                            • {e.start} – {e.end}
                          </span>
                        </TimelineMeta>
                        {e.summary && <TimelineBody>{e.summary}</TimelineBody>}
                        {e.bullets && e.bullets.length > 0 && (
                          <TimelineBody>
                            <ul>
                              {e.bullets.map((b, i) => (
                                <li key={i}>{b}</li>
                              ))}
                            </ul>
                          </TimelineBody>
                        )}
                        {e.stack && e.stack.length > 0 && (
                          <StackRow>
                            {e.stack.map((s) => (
                              <StackTag key={s}>{s}</StackTag>
                            ))}
                          </StackRow>
                        )}
                      </TimelineItem>
                    ))}
                  </Timeline>
                </GlassCardInner>
              </GlassCard>
            </RevealSection>

            {/* EDUCAÇÃO */}
            {profileData.education.length > 0 && (
              <RevealSection delay={0.1}>
                <GlassCard
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                >
                  <GlassCardInner>
                    <SectionTitle>
                      <FaGraduationCap />
                      EDUCAÇÃO
                    </SectionTitle>

                    <Timeline>
                      {profileData.education.map((ed) => (
                        <TimelineItem key={`${ed.school}-${ed.degree}`}>
                          <TimelineRole>{ed.school}</TimelineRole>
                          <TimelineMeta>
                            <span>{ed.degree}</span>
                            {(ed.start || ed.end) && (
                              <span>
                                • {ed.start ?? ""}
                                {ed.start && ed.end ? " – " : ""}
                                {ed.end ?? ""}
                              </span>
                            )}
                          </TimelineMeta>
                          {ed.details && ed.details.length > 0 && (
                            <TimelineBody>
                              <ul>
                                {ed.details.map((d, i) => (
                                  <li key={i}>{d}</li>
                                ))}
                              </ul>
                            </TimelineBody>
                          )}
                        </TimelineItem>
                      ))}
                    </Timeline>
                  </GlassCardInner>
                </GlassCard>
              </RevealSection>
            )}

            {/* CERTIFICAÇÕES */}
            {profileData.certifications.length > 0 && (
              <RevealSection delay={0.14}>
                <GlassCard
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                >
                  <GlassCardInner>
                    <SectionTitle>
                      <FaShieldAlt />
                      CERTIFICAÇÕES
                    </SectionTitle>

                    <CertGrid>
                      {profileData.certifications.map((c) => (
                        <CertCard key={`${c.org}-${c.name}`}>
                          <CertIcon>
                            <FaTrophy size={20} />
                          </CertIcon>
                          <CertText>
                            <AchievementRibbon>
                              Conquista desbloqueada
                            </AchievementRibbon>
                            <strong>{c.name}</strong>
                            <Small>
                              {c.org}
                              {c.year ? ` • ${c.year}` : ""}
                            </Small>
                            {c.url && (
                              <Small>
                                <a
                                  href={c.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: "var(--cyan)" }}
                                >
                                  ver credencial
                                </a>
                              </Small>
                            )}
                          </CertText>
                        </CertCard>
                      ))}
                    </CertGrid>
                  </GlassCardInner>
                </GlassCard>
              </RevealSection>
            )}

            {/* CONTATO / LINKS */}
            <RevealSection delay={0.18}>
              <GlassCard
                $accent
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
              >
                <GlassCardInner>
                  <SectionTitle>
                    <FaEnvelope />
                    CONTATO
                  </SectionTitle>

                  <ContactList>
                    {profileData.socials.map((s) => (
                      <ContactLink
                        key={s.label}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ContactIcon>{iconForSocial(s.label)}</ContactIcon>
                        <span>{s.url.replace(/^mailto:/, "")}</span>
                      </ContactLink>
                    ))}
                  </ContactList>
                </GlassCardInner>
              </GlassCard>
            </RevealSection>
          </Column>
        </Grid>
      </Container>
    </PageWrapper>
  );
};

export default AboutMe;
